import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { GET_USERS_QUERY } from "@/apis/gql/apolloClient";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { UserAccess } from "../user-access";
import { UserBasicData } from "../user-basic-data";

import { StepIndicator } from "./step-indicator";

import { useSession } from "@/lib/auth/auth";
import { UserAccountingFirmRole } from "@/lib/users/user-roles";
import { USER_COMPANIES_QUERY } from "@/queries/companies/get-all";
import {
  useCreateAccountingFirmUser,
  useCreateCompanyUser,
} from "@/queries/users/create";
import { type FormValues, formSchema } from "../formSchema";
import { UserTypeStep } from "../user-type";

interface CreateUserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateUserModal({ open, onOpenChange }: CreateUserModalProps) {
  const { t } = useTranslation();
  const session = useSession();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const [handleCreateAccountingFirmUser, { loading: creatingAccounting }] =
    useCreateAccountingFirmUser([
      { query: GET_USERS_QUERY, variables: { withCompanyUsers: true } },
      USER_COMPANIES_QUERY,
    ]);
  const [handleCreateCompanyUser, { loading: creatingCompany }] =
    useCreateCompanyUser([
      { query: GET_USERS_QUERY, variables: { withCompanyUsers: true } },
      USER_COMPANIES_QUERY,
    ]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    defaultValues: {
      userType: "accounting_firm",
      name: "",
      email: "",
      accountingFirmRole: UserAccountingFirmRole.USER,
      companyAccess: [],
    },
  });

  useEffect(() => {
    if (!open) {
      setCurrentStep(1);
      setIsSubmitting(false);
      form.reset();
    }
  }, [open, form]);

  const handleStepClick = async (step: number) => {
    if (step < currentStep) {
      setCurrentStep(step);
      return;
    }

    if (step === currentStep + 1) {
      await handleNextStep();
    }
  };

  const handleNextStep = async () => {
    if (currentStep === 1) {
      setCurrentStep(2);
      return;
    }

    if (currentStep === 2) {
      const userType = form.getValues("userType");
      const fieldsToValidate =
        userType === "company"
          ? ["name", "email"]
          : ["name", "email", "accountingFirmRole"];

      const isValid = await form.trigger(fieldsToValidate as any);
      if (isValid) {
        setCurrentStep(3);
      }
      return;
    }
  };

  const handlePreviousStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const onSubmit = async (data: FormValues) => {
    const accountingFirmId =
      session.data?.user?.accountingFirms?.[0]?.accountingFirmId;
    setIsSubmitting(true);
    try {
      if (data.userType === "accounting_firm") {
        if (!accountingFirmId)
          throw new Error("Accounting firm ID is required");
        await handleCreateAccountingFirmUser({
          userData: {
            name: data.name,
            email: data.email,
            accountingFirmRole: {
              accountingFirmId,
              role: data.accountingFirmRole ?? UserAccountingFirmRole.USER,
            },
            companyAccess: data.companyAccess?.length
              ? data.companyAccess
              : undefined,
          },
        });
      } else {
        await handleCreateCompanyUser({
          userData: {
            name: data.name,
            email: data.email,
            companyAccess: data.companyAccess,
          },
        });
      }
      onOpenChange(false);
      toast.success(t("users.user_created_successfully"));
    } catch (error) {
      toast.error(t("users.error_creating_user"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[800px] w-[90vw] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{t("users.create_new_user")}</DialogTitle>
        </DialogHeader>
        <StepIndicator
          currentStep={currentStep}
          onStepClick={handleStepClick}
        />
        <div className="flex-1 overflow-y-auto pr-2">
          <Form {...form}>
            <form className="space-y-6 flex flex-col min-h-full">
              <div className="flex-1">
                {currentStep === 1 && <UserTypeStep form={form} />}
                {currentStep === 2 && <UserBasicData form={form} />}
                {currentStep === 3 && <UserAccess form={form} />}
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                {currentStep > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handlePreviousStep}
                  >
                    {t("back")}
                  </Button>
                )}

                {currentStep < 3 ? (
                  <Button type="button" onClick={handleNextStep}>
                    {t("next")}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    disabled={
                      isSubmitting || creatingAccounting || creatingCompany
                    }
                    onClick={async () => {
                      const isValid = await form.trigger();
                      if (isValid) {
                        const data = form.getValues();
                        await onSubmit(data);
                      }
                    }}
                  >
                    {isSubmitting || creatingAccounting || creatingCompany
                      ? t("users.creating")
                      : t("users.create_new_user")}
                  </Button>
                )}
              </div>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
